import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import groupBy from 'lodash.groupby';
import sortBy from 'lodash.sortby';

import { PERIOD } from '../../constants/enrollment';
import {
    PM_RECOMMENDATION_FREQUENCY,
    MEASURE_NAMES,
    SORTED_LOWEST_SKILL_NAMES,
} from '../../constants/assessments';
import { getMeasureScores } from '../utils/studentsListHelpers';
import { getStartDay, parseDate } from '../utils/assessmentWindowsUtils';
import BMRecommendationRules, {
    REQUIRED_SKILLS_FOR_RECOMMENDATIONS,
    INTERPRETATIONS_AT_RISK,
    GATING_RULES_AT_RISK,
} from './lecturaRecommendationRules';
import { getMeasureScoreBySkillName } from '../utils/measureScoreBySkillName';
import { IDEL2_INTERPRETATIONS } from '../../constants/interpretations';
import { getGatingRules } from '../gating-rules/getGatingRules';
import { IDEL2 } from '../../constants/applications';
import { getGatedMeasuresStatusForLectura } from '../gating-rules/getGatedMeasuresStatus';

const getLowestSkillMeasure = measures => {
    const sortedMeasures = measures.sort(
        (current, next) =>
            SORTED_LOWEST_SKILL_NAMES.indexOf(current) - SORTED_LOWEST_SKILL_NAMES.indexOf(next),
    );

    if (sortedMeasures.includes(MEASURE_NAMES.FSL) && sortedMeasures.includes(MEASURE_NAMES.FSS)) {
        return [MEASURE_NAMES.FSS, MEASURE_NAMES.FSL];
    }

    return sortedMeasures.splice(0, 1);
};

const getGatingRuleType = (gatingRules, measure) => {
    if (gatingRules.isRuleMet && gatingRules.gatedMeasures.includes(measure)) {
        return gatingRules.ruleType;
    }
    return '';
};

/**
 * Check if recommendation rule conditions are met.
 * @param {Object} recommendationRule { atRisk: {Array}, recommendedMeasures: {Array} },
 * @param {Object} studentResults student results summary: {<measure>: <summary>}
 * @returns {Boolean} true if rule is met, false otherwise
 */
const isRuleApplicable = (recommendationRule, studentResults) =>
    // all measures from rule are at risk at the same time
    recommendationRule?.atRisk.every(
        measure => studentResults[measure] && studentResults[measure].atRisk,
    );

/**
 * Walk through all recommendation rules and find first one that matches
 * results status. Take recommended measures for it.
 * @param {String} grade student's grade
 * @param {Object} studentResults by measure  {<measure>: <result summary>}
 * @returns {Array} with recommended measures or empty array if no applicable rule
 */
const applyRecommendationRulesOnResults = (grade, studentResults) => {
    const applicableRule = get(BMRecommendationRules, grade, []).filter(rule =>
        isRuleApplicable(rule, studentResults),
    );

    const measures = applicableRule.reduce(
        (allItem, item) => [...new Set([...allItem, item.recommendedMeasures[0]])],
        [],
    );

    if (measures.length > 1 && !measures.includes(MEASURE_NAMES.CP)) {
        return getLowestSkillMeasure(measures);
    }

    if (measures.length > 1 && measures.includes(MEASURE_NAMES.FEP)) {
        return [MEASURE_NAMES.FEP];
    }

    return measures;
};

/**
 * Gets results sorting date according to selected measure
 */
const getResultSortingDate = ({ createDate, clientModDate }, measure) =>
    measure === MEASURE_NAMES.CP ? createDate : clientModDate;

/**
 * Gets latest result for selected measure
 * @param {Array<Object>} results for period
 * @param {String} measure we need to filter results by
 * @returns {Object} latest result
 */
const getLatestMeasureResult = (results, measure) => {
    let latestResult = null;

    const measureResults = results.filter(result => result.measure === measure);

    if (measureResults.length) {
        // sort results by date and slice the last one
        [latestResult] = sortBy(measureResults, result =>
            parseDate(getResultSortingDate(result, measure)),
        ).slice(-1);
    }

    return latestResult;
};

const getResultsStatuses = (studentSkills, results, grade, period, gatedMeasuresStatus) => {
    const gatingRules = getGatingRules(results, grade, period, gatedMeasuresStatus, IDEL2);
    const mappedResults = groupBy(results, 'measure');
    return studentSkills.reduce((allResults, { skillName, measureName }) => {
        const measureScoreKey = getMeasureScoreBySkillName(skillName, measureName);
        const measureScore = getMeasureScores(results, measureName, measureScoreKey);

        if (!measureScore) {
            const gatingRuleType = getGatingRuleType(gatingRules[0], measureScoreKey);

            return {
                ...allResults,
                [measureScoreKey]: {
                    clientModDate: undefined,
                    completed: !(gatingRuleType === ''),
                    atRisk: GATING_RULES_AT_RISK.includes(gatingRuleType),
                    isGatingRulesProduced: false,
                },
            };
        }

        return {
            ...allResults,
            [measureScoreKey]: {
                clientModDate: get(mappedResults, `[${measureName}][0].clientModDate`),
                completed: Object.values(IDEL2_INTERPRETATIONS).includes(
                    measureScore.interpretation,
                ),
                atRisk: INTERPRETATIONS_AT_RISK.includes(measureScore.interpretation),
                isGatingRulesProduced: false,
            },
        };
    }, []);
};

/**
 * Find latest BM period with completed results for all observational measures,
 * including those that were assessed and those with imputed scores.
 * Based on performance in this BM period, PM recommendation will be made.
 *
 * @returns {Object} results summary for completed BM period or {} when no completed BM
 * @param student
 * @param studentResults
 */
const getCompletedResults = (student, studentResults) => {
    // TODO add optional filter by skill name
    const { appStatuses, grade } = student;
    const studentSkills = REQUIRED_SKILLS_FOR_RECOMMENDATIONS[grade];

    // Searching results from EOY to BOY as we need the last completed period
    // eslint-disable-next-line no-restricted-syntax
    for (const period of [PERIOD.EOY, PERIOD.MOY, PERIOD.BOY]) {
        const resultsByPeriod = get(studentResults, period, []);
        // no results for period, try next one
        if (!resultsByPeriod.length) {
            // eslint-disable-next-line no-continue
            continue;
        }

        const gatedMeasuresStatus = getGatedMeasuresStatusForLectura(appStatuses, period);

        const resultStatuses = getResultsStatuses(
            studentSkills,
            resultsByPeriod,
            grade,
            period,
            gatedMeasuresStatus,
        );

        // Last completed period is met
        if (
            Object.keys(resultStatuses)
                .filter(measure => measure !== MEASURE_NAMES.CP)
                .every(measure => resultStatuses[measure].completed)
        ) {
            return resultStatuses;
        }
    }
    return {};
};

/**
 * Get Lectura recommended measures based on BM performance only.
 * High level rules:
 * - recommend measures based on latest completed BM assessment (all obsv measures)
 * - take into probably account imputed scores of gated measures
 * - ignore CP result completeness and performance
 * - ignore invalidated BM and PM results
 * - stop recommending a measure if it was PM'ed
 * More here: https://docs.google.com/document/d/1_XcXcQ4DdmhcA9S5sO9z8N1Exel-vf6WxRyN4dz-Mqg/edit#heading=h.kp4f5nxwtx32
 * @param {Object} student student data with Lectura app statuses including
 *                         interp and gating status
 * @param {Object} eligibleResults contains all eligible student results grouped by period
 * @returns {Array<String>} ['LSS', 'FSL'] or [] if nothing to recommend
 */
const getLecturaBMRecommendedMeasures = (student, eligibleResults) => {
    // Retrieve all completed results from last completed BM period
    const completedBMResults = getCompletedResults(student, eligibleResults);
    // nothing was assessed, nothing to recommend
    if (isEmpty(completedBMResults)) {
        return [];
    }

    // get all BM recommended measures based on student's grade and completed BM results
    return applyRecommendationRulesOnResults(student.grade, completedBMResults);
};

/**
 * Get Lectura recommended measures based on BM performance and PM results.
 * Basically filters BM recommended measures considering recent PM results.
 * @param {Object} student student data with Lectura app statuses including
 *                         interp and gating status
 * @param {Object} eligibleResults contains all eligible student results grouped by period
 * @returns {Array<String>} ['LSS', 'FSL'] or [] if nothing to recommend
 */
export const getLecturaRecommendedMeasures = (student, eligibleResults) => {
    const recommendedMeasures = [];
    // get all BM recommended measures
    const bmRecommendedMeasures = getLecturaBMRecommendedMeasures(student, eligibleResults);
    // get today's date using startOf('day'), it gives us 00:00:00 for accurate days calculation
    const todayDate = getStartDay(new Date());
    // go through all BM recommended measures
    bmRecommendedMeasures.forEach(measure => {
        // get latest PM result for this measure
        const latestPMResult = getLatestMeasureResult(eligibleResults[PERIOD.PM], measure);
        if (latestPMResult) {
            // if there is some PM result, get its date
            const date = getResultSortingDate(latestPMResult, measure);
            const resultDate = getStartDay(date);
            // and if enough amount of days passed, add this measure into list of recommendations
            if (todayDate.diff(resultDate, 'days') >= PM_RECOMMENDATION_FREQUENCY) {
                recommendedMeasures.push(measure);
            }
        } else {
            // if there is no PM result, add this measure into list of recommendations
            recommendedMeasures.push(measure);
        }
    });

    return recommendedMeasures;
};
